import { useIsomorphicEffect } from 'hooks/useIsomorphicEffect'
import { memo, useRef } from 'react'
import { useToggleSidebar } from 'state/app/hooks'
import styled from 'styled-components'
import theme from 'theme'
import { overlayBreakpointMap } from 'theme/overlay'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import { calculateScrollbarWidth } from 'utils/scroll'
import Column from '../Column'
import SidebarFeatures from './SidebarFeatures'
import SideBarHeader from './SidebarHeader'
import { SidebarToggle } from './SidebarToggle'

const SidebarContent = () => {
  const { isSidebarOpen, featureActive, closeSidebar } = useToggleSidebar()
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    isSidebarOpen && (
      <>
        <SideBarHeader feature={featureActive} containerRef={containerRef} onClose={closeSidebar} />

        <StyledScrollContainer
          minWidth={['320px', '320px', theme.sidebarWidth]}
          maxHeight={['unset', 'unset', 'calc(100% - 45px)']}
          ref={containerRef}
        >
          <SidebarFeatures feature={featureActive} />
        </StyledScrollContainer>
      </>
    )
  )
}

export const SidebarContainer: React.FC<BoxProps> = ({ children, zIndex, ...props }) => {
  const { isSidebarOpen } = useToggleSidebar()

  useIsomorphicEffect(() => {
    calculateScrollbarWidth()
  }, [])

  return (
    <StyledSidebarContainer
      $Open={isSidebarOpen}
      zIndex={zIndex}
      minHeight="calc(100% - var(--notification-bar-height))"
      maxHeight="calc(100% - var(--notification-bar-height))"
    >
      <SidebarToggle />

      <StyledInnerContainer $Open={isSidebarOpen} {...props} zIndex={zIndex}>
        {children}
      </StyledInnerContainer>
    </StyledSidebarContainer>
  )
}

const Sidebar: React.FC = () => {
  return (
    <SidebarContainer id="sidebar-navigation" height={['100%', '', 'calc(100% - 65px)']}>
      <SidebarContent />
    </SidebarContainer>
  )
}

const StyledSidebarContainer = styled(Box)<{ $Open: boolean }>`
  position: fixed;
  top: 0px;
  bottom: 0px;
  right: 0;
  width: ${({ $Open }) => ($Open ? '100vw' : '0')};
  transition: ${({ theme: { transitions } }) => transitions.medium};
  z-index: 99;
  height: 100%;

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    top: calc(${({ theme: { topbarHeight } }) => topbarHeight} + var(--notification-bar-height));
    right: 0;
    bottom: 0;
    padding-top: 0px;
    left: unset;
    transition: ${({ theme: { transitions } }) => transitions.fast};
    width: ${({ theme: { sidebarWidth }, $Open }) => ($Open ? `${sidebarWidth}px` : '0')};
    padding-bottom: 0px;
  }

  @media screen and (min-width: ${overlayBreakpointMap.sidebarOverlay}px) {
    position: unset;
  }
`

const StyledScrollContainer = styled(Box)`
  overflow-y: scroll;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: 0.9rem;
  }

  ::-webkit-scrollbar-thumb {
    visibility: hidden;
  }

  &:hover {
    ::-webkit-scrollbar-thumb {
      background-color: #2d3137 !important;
      visibility: visible;
    }
  }
  flex: 1;
`

const StyledInnerContainer = styled(Column)<{ $Open: boolean }>`
  background: ${({ theme: { colors } }) => colors.cardBackground};

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    right: ${({ $Open }) => ($Open ? '0px' : 'calc(-56px - var(--scrollbar-width))')};
    z-index: 100;
    position: relative;
  }
`
export default memo(Sidebar)
